/**
 * RowActionsMenu — the kebab shell every V2 table row uses: the trigger, open/close,
 * and placement. The items stay with each table (docs/DUPLICATION_AUDIT.md, B23).
 *
 *   <RowActionsMenu label={`Actions for ${area.area_name}`} classPrefix="ap">
 *     {(close) => (
 *       <button className="ap-row-actions__item" onClick={() => { onEdit(area); close(); }}>Edit</button>
 *     )}
 *   </RowActionsMenu>
 *
 * Placement. The menu is `position: fixed`, measured from the trigger, so a table's
 * `overflow` cannot clip it; TemplatesTable (absolute inside the cell) and
 * PreformNotesTab (unpositioned) were clipped. It opens below and flips above when its
 * own measured height does not fit, rather than at a guessed threshold: the old copies
 * used 120 to 200px, so a short menu near the bottom flipped needlessly and a long one
 * still ran off the screen.
 *
 * Closing. Outside click, any scroll (a fixed menu would detach from its row), Escape,
 * or `close()` from an item. `holdOpen` suspends all of them, for an item that is
 * busy (UsersTable's SIP registration shows its spinner in the menu).
 *
 * @param {string} label — the trigger's aria-label
 * @param {'ap'|'rp'|'dp'|'up'|'ct'} [classPrefix] — the table family's stylesheet
 *        (`${prefix}-row-actions`, `__trigger`, `__menu`)
 * @param {'right'|'left'} [align] — which trigger edge the menu lines up with; 'left'
 *        for a kebab in a row's first column, so the menu opens into the row
 * @param {boolean} [holdOpen]
 * @param {object} [triggerProps] — extra trigger attributes (e.g. data-help-id)
 * @param {(close: () => void) => node | node} children — the menu items
 */

import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import BlockedReasonTip from './BlockedReasonTip.jsx';
import './row-actions-menu.css';

const GAP = 4;

const RowActionsMenu = ({
    label = 'Actions', classPrefix = 'ap', align = 'right', holdOpen = false, triggerProps = null, children,
}) => {
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState({});
    const wrapRef = useRef(null);
    const menuRef = useRef(null);
    const holdRef = useRef(holdOpen);
    holdRef.current = holdOpen;

    const close = useCallback(() => setOpen(false), []);

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => {
            if (holdRef.current) return;
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        const onScroll = () => { if (!holdRef.current) setOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape' && !holdRef.current) setOpen(false); };
        document.addEventListener('mousedown', onDown);
        window.addEventListener('scroll', onScroll, true);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            window.removeEventListener('scroll', onScroll, true);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    /* Placed before paint: below the trigger, or above it when the menu's measured
       height does not fit below and there is more room above. */
    useLayoutEffect(() => {
        if (!open || !wrapRef.current || !menuRef.current) return;
        const rect = wrapRef.current.getBoundingClientRect();
        const h = menuRef.current.offsetHeight;
        const below = window.innerHeight - rect.bottom - GAP;
        const above = rect.top - GAP;
        const up = h > below && above > below;
        const horizontal = align === 'left'
            ? { left: `${rect.left}px`, right: 'auto' }
            : { right: `${window.innerWidth - rect.right}px`, left: 'auto' };
        setMenuStyle({
            position: 'fixed',
            ...horizontal,
            top: up ? 'auto' : `${rect.bottom + GAP}px`,
            bottom: up ? `${window.innerHeight - rect.top + GAP}px` : 'auto',
            margin: 0,
            zIndex: 9999,
        });
    }, [open, align]);

    const handleToggle = (e) => {
        /* Rows can be clickable; opening the menu must not also open the row. */
        e.stopPropagation();
        if (open && holdRef.current) return;
        if (!open) setMenuStyle({ position: 'fixed', visibility: 'hidden', zIndex: 9999 }); // measured, then placed
        setOpen((v) => !v);
    };

    const p = `${classPrefix}-row-actions`;
    return (
        <div className={p} ref={wrapRef}>
            <button
                type="button"
                className={`${p}__trigger`}
                onClick={handleToggle}
                aria-label={label}
                aria-expanded={open}
                {...(triggerProps || {})}
            >
                <i className="fe fe-more-vertical" aria-hidden="true" />
            </button>
            {open ? (
                <div className={`${p}__menu`} style={menuStyle} ref={menuRef}>
                    {typeof children === 'function' ? children(close) : children}
                </div>
            ) : null}
        </div>
    );
};

/**
 * A menu item that may be blocked, e.g. Delete Area while a device in it is alarming.
 * Blocked, it stays visible but greyed (`is-blocked`), says why in a popover on hover
 * or tap (BlockedReasonTip: a `title` tooltip takes about a second to appear, and a
 * tablet has no hover at all), keeps the reason for screen readers, and ignores the
 * click, so the page's own guard does not answer the same click with a toast as well.
 * `aria-disabled` rather than `disabled`: a disabled button fires no pointer events,
 * so the popover would never open.
 *
 * @param {string|null} reason — why it is blocked; falsy = a normal item
 * @param {string} className — the item's classes (`${prefix}-row-actions__item …`)
 * @param {() => void} onClick — runs only when not blocked
 */
export const BlockableMenuItem = ({ reason, className, onClick, children, ...rest }) => (
    <BlockedReasonTip message={reason || ''} block>
        <button
            type="button"
            className={`${className}${reason ? ' is-blocked' : ''}`}
            aria-disabled={reason ? true : undefined}
            onClick={() => { if (!reason) onClick(); }}
            {...rest}
        >
            {children}
            {reason ? <span className="row-actions-sr-only">{reason}</span> : null}
        </button>
    </BlockedReasonTip>
);

export default RowActionsMenu;
